import { useState, useRef, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

type DropdownPosition = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

export type DropdownItemProps = {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
};

export type DropdownProps = {
  trigger: ReactNode;
  children?: ReactNode;
  items?: DropdownItemProps[];
  position?: DropdownPosition;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;
  menuClassName?: string;
};

const positionStyles: Record<DropdownPosition, string> = {
  'bottom-start': 'top-full left-0 mt-1',
  'bottom-end': 'top-full right-0 mt-1',
  'top-start': 'bottom-full left-0 mb-1',
  'top-end': 'bottom-full right-0 mb-1',
};

function Dropdown({
  trigger,
  children,
  items,
  position = 'bottom-start',
  isOpen: controlledIsOpen,
  onOpenChange,
  className,
  menuClassName,
}: DropdownProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setIsOpen = useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setInternalIsOpen(value);
      }
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange]
  );

  const handleTriggerClick = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item: DropdownItemProps) => {
    if (!item.disabled) {
      item.onClick?.();
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen]);

  return (
    <div ref={dropdownRef} className={clsx('relative inline-flex', className)}>
      <div onClick={handleTriggerClick}>{trigger}</div>
      {isOpen && (
        <div
          className={clsx(
            'absolute z-50 min-w-[180px]',
            positionStyles[position],
            'animate-in fade-in zoom-in-95 duration-150',
            menuClassName
          )}
        >
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-600 py-1 overflow-hidden">
            {children ||
              items?.map((item, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                  className={clsx(
                    'w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors',
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : item.danger
                        ? 'text-error hover:bg-error/10 dark:text-error-lighter dark:hover:bg-error/20'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-dark-200 dark:hover:bg-dark-700'
                  )}
                >
                  {item.icon && <span className="shrink-0">{item.icon}</span>}
                  {item.label}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownItem({
  children,
  onClick,
  disabled,
  danger,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : danger
            ? 'text-error hover:bg-error/10 dark:text-error-lighter dark:hover:bg-error/20'
            : 'text-gray-700 hover:bg-gray-100 dark:text-dark-200 dark:hover:bg-dark-700',
        className
      )}
    >
      {children}
    </button>
  );
}

function DropdownDivider() {
  return <div className="my-1 h-px bg-gray-200 dark:bg-dark-600" />;
}

Dropdown.displayName = 'Dropdown';
DropdownItem.displayName = 'DropdownItem';
DropdownDivider.displayName = 'DropdownDivider';

export { Dropdown, DropdownItem, DropdownDivider };
